/**
 * Mouth shapes from spelling, rather than from a recording
 * ([Mascot](../../../docs/MASCOT.md#lip-sync)).
 *
 * [D-013](../../../docs/DECISIONS.md#d-013) assumed every line would be recorded
 * and the timeline generated from the audio. There is no voice actor
 * ([D-081](../../../docs/DECISIONS.md#d-081)), and the app already speaks every
 * line with the device's own English voice — which on an iPad is a better voice
 * than anything a build machine can synthesise.
 *
 * So the timeline comes from the **phonemes the course already carries**. Every
 * lexeme has them, because the scheduler uses them for the pronunciation gap
 * map; nothing new has to be authored, and a word that has never been recorded
 * still has a mouth shape for every sound in it.
 *
 * **What this can and cannot promise.** The *sequence* is right — it comes from
 * the word. The *timing* is an estimate, because the device voice speaks at its
 * own pace and nothing here has heard it. That is why the track is emitted as
 * proportions of a whole rather than as seconds: whoever plays it knows how long
 * the line actually took, and this does not.
 */
/** The eight shapes, plus rest. Exactly the set in Mascot. */
export const VISEMES = ['rest', 'AI', 'E', 'O', 'U', 'MBP', 'FV', 'L'];
/**
 * Which shape a sound is made with.
 *
 * Keyed by the IPA the lexemes carry. Several sounds share a shape and that is
 * the point of visemes: a mouth cannot tell *p* from *b*, so neither should the
 * drawing.
 */
const SHAPE = {
    // Closed. The one people notice when it is wrong.
    m: 'MBP',
    b: 'MBP',
    p: 'MBP',
    // Teeth on lip.
    f: 'FV',
    v: 'FV',
    // Tongue visible.
    l: 'L',
    θ: 'L',
    ð: 'L',
    n: 'L',
    t: 'L',
    d: 'L',
    // Rounded.
    u: 'U',
    uː: 'U',
    ʊ: 'U',
    w: 'U',
    ʊə: 'U',
    o: 'O',
    oʊ: 'O',
    əʊ: 'O',
    ɔ: 'O',
    ɔː: 'O',
    ɒ: 'O',
    ɔɪ: 'O',
    // Wide open.
    ɑ: 'AI',
    ɑː: 'AI',
    æ: 'AI',
    ʌ: 'AI',
    aɪ: 'AI',
    aʊ: 'AI',
    a: 'AI',
    // Medium, wide.
    i: 'E',
    iː: 'E',
    ɪ: 'E',
    e: 'E',
    eɪ: 'E',
    ɛ: 'E',
    ə: 'E',
    ɜ: 'E',
    ɜː: 'E',
    eə: 'E',
    ɪə: 'E',
    j: 'E',
    // Swedish ([D-190](../../../docs/DECISIONS.md#d-190)). Its rounded front
    // vowels — *y*, *ö*, the *u* of *hus* — are said with the lips pushed out,
    // which is the whole of what makes them hard for a Danish mouth, so they
    // are drawn rounded. The *sj*-sound rounds too. The retroflexes are the
    // tongue's; *å* is round, long *ä* before *r* wide open.
    y: 'U',
    yː: 'U',
    ʏ: 'U',
    ø: 'U',
    øː: 'U',
    œ: 'U',
    œː: 'U',
    ʉ: 'U',
    ʉː: 'U',
    ɵ: 'U',
    ɧ: 'U',
    ʂ: 'U',
    oː: 'O',
    eː: 'E',
    ɛː: 'E',
    æː: 'AI',
    ʈ: 'L',
    ɖ: 'L',
    ɳ: 'L',
    ɭ: 'L',
};
/**
 * Roughly how long a sound takes, relative to the others.
 *
 * Unitless on purpose. A vowel holds, a stop is a tap, and everything else sits
 * between — which is enough to make a mouth look like it is speaking. Absolute
 * milliseconds would be a guess dressed up as a measurement.
 */
const WEIGHT = {
    rest: 1,
    AI: 2.2,
    E: 1.9,
    O: 2.1,
    U: 1.9,
    MBP: 1,
    FV: 1.2,
    L: 1.1,
};
/** Anything not in the table: a consonant with no strong shape of its own. */
const DEFAULT_SHAPE = 'E';
export const NO_TRACK = { frames: [{ at: 0, v: 'rest' }] };
/** The shape one sound is made with. */
export const visemeFor = (phoneme) => SHAPE[phoneme] ?? SHAPE[phoneme.normalize('NFC')] ?? DEFAULT_SHAPE;
/**
 * Turn a run of sounds into a mouth.
 *
 * **Repeats are collapsed.** Two sounds sharing a shape are one hold, not two
 * identical frames — a mouth that re-forms a shape it is already in reads as a
 * stutter, and it is the single most common way generated lip sync looks wrong.
 *
 * Always starts and ends closed, because a face left mid-vowel looks stunned.
 */
export function track(phonemes) {
    const shapes = phonemes.map(visemeFor);
    if (shapes.length === 0)
        return NO_TRACK;
    // Collapse, keeping the total weight of everything collapsed into one hold.
    const held = [];
    for (const v of shapes) {
        const last = held.at(-1);
        if (last !== undefined && last.v === v)
            last.weight += WEIGHT[v];
        else
            held.push({ v, weight: WEIGHT[v] });
    }
    // A closing rest, so the line ends with his mouth shut.
    held.push({ v: 'rest', weight: WEIGHT.rest });
    const total = held.reduce((sum, hold) => sum + hold.weight, 0);
    const frames = [];
    let at = 0;
    for (const hold of held) {
        frames.push({ at: Math.round((at / total) * 1000) / 1000, v: hold.v });
        at += hold.weight;
    }
    return { frames };
}
/**
 * The mouth at a moment, given how far through the line we are.
 *
 * `progress` is 0–1. Anything outside that is clamped rather than rejected: it
 * arrives from a clock, and a clock that overshoots by a frame should close his
 * mouth rather than throw.
 */
export function visemeAt(line, progress) {
    if (progress >= 1)
        return 'rest';
    const at = progress <= 0 ? 0 : progress;
    let current = 'rest';
    for (const frame of line.frames) {
        if (frame.at > at)
            break;
        current = frame.v;
    }
    return current;
}
/**
 * A whole sentence, from the words the course knows.
 *
 * Words it does not know still move his mouth: an unknown word becomes a short
 * run of open-and-close rather than a pause, because a mouth that stops while a
 * voice carries on is worse than a mouth that is roughly right.
 */
export function speak(words) {
    const guessed = ['E', 'AI', 'E'];
    const all = words.flatMap((phonemes) => phonemes === undefined || phonemes.length === 0 ? guessed : [...phonemes]);
    return track(all);
}
/**
 * Split a transcription into sounds.
 *
 * The course stores phonemes per word, but the app only ever *sees* IPA — it is
 * what the hint card shows, so it is already on the wire. Parsing it here means
 * a mouth costs no extra request and no schema change.
 *
 * Stress and length marks go: `ˈ` and `ˌ` mark a syllable rather than a sound,
 * and `ː` belongs to the vowel before it, which the table already keys on.
 * Diphthongs are two letters and one mouth, so they are matched first — taking
 * `a` and `ɪ` separately would make *my* open twice.
 */
const DIPHTHONGS = [
    'aɪ',
    'aʊ',
    'eɪ',
    'oʊ',
    'əʊ',
    'ɔɪ',
    'ɪə',
    'eə',
    'ʊə',
    'iː',
    'ɑː',
    'ɔː',
    'uː',
    'ɜː',
];
export function ipaPhonemes(ipa) {
    const out = [];
    // `slice` rather than an index, so there is no can't-happen undefined branch
    // to leave uncovered: a one-character slice of a non-empty string is a string.
    let rest = ipa.replace(/[ˈˌ.\s]/g, '');
    while (rest.length > 0) {
        const take = DIPHTHONGS.includes(rest.slice(0, 2)) ? 2 : 1;
        out.push(rest.slice(0, take));
        rest = rest.slice(take);
    }
    return out;
}
//# sourceMappingURL=visemes.js.map